import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { colors, font } from "../styles/theme";
import { services, NEUTRAL_SERVICE } from "../data/services";
import useLeadSubmit from "../lib/useLeadSubmit";
import usePhoneMask from "../lib/usePhoneMask";
import useScrollLock from "../hooks/useScrollLock";
import ConsentCheckbox from "./ConsentCheckbox";

// Единый попап «Заявка». Открывается из кнопки в шапке и из CTA на странице услуг.
export default function RequestModal({ open, onClose }) {
  const { status, error, errors, submit, clearFieldError } = useLeadSubmit("Попап «Заявка»");
  const phone = usePhoneMask();
  const [closing, setClosing] = useState(false);

  useScrollLock(open);

  // Мягкое закрытие: проигрываем анимацию, затем размонтируем.
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 200);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && handleClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div
      onClick={handleClose}
      className={closing ? "ovl-fade-out" : "ovl-fade-in"}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(8,8,11,.62)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={closing ? "ovl-panel-out" : "ovl-panel-in"}
        style={{
          width: 440,
          maxWidth: "100%",
          background: colors.white,
          borderRadius: 18,
          boxShadow: "0 30px 80px rgba(0,0,0,.5)",
          padding: "32px 30px 28px",
          position: "relative",
        }}
      >
        <button
          onClick={handleClose}
          aria-label="Закрыть"
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 38,
            height: 38,
            border: "none",
            borderRadius: 10,
            background: "#f1f2f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={20} color={colors.ink} />
        </button>

        {status !== "success" && (
          <>
            <h3
              style={{
                fontFamily: font.display,
                fontWeight: 700,
                fontSize: 26,
                textTransform: "uppercase",
                margin: "4px 0 0",
                color: colors.ink,
              }}
            >
              Оставьте заявку
            </h3>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: colors.muted }}>
              Перезвоним в течение 15 минут и бесплатно проконсультируем.
            </p>
          </>
        )}

        {status === "success" ? (
          <div style={{ padding: "30px 0 10px", textAlign: "center" }}>
            <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 24, textTransform: "uppercase", color: colors.ink }}>
              Заявка отправлена!
            </div>
            <p style={{ margin: "10px 0 0", fontSize: 14, color: colors.text }}>
              Мы свяжемся с вами в течение 15 минут.
            </p>
          </div>
        ) : (
          <>
            <form noValidate onSubmit={(e) => submit(e)} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 22 }}>
              <div>
                <Input name="name" placeholder="Ваше имя" invalid={!!errors.name} onChange={() => clearFieldError("name")} />
                {errors.name && <FieldError text={errors.name} />}
              </div>
              <div>
                <Input
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="+7 (___) ___-__-__"
                  invalid={!!errors.phone}
                  value={phone.value}
                  onChange={(e) => {
                    phone.onChange(e);
                    clearFieldError("phone");
                  }}
                  onFocus={phone.onFocus}
                />
                {errors.phone && <FieldError text={errors.phone} />}
              </div>
              <Select name="service">
                <option value="">Выберите услугу</option>
                <option>{NEUTRAL_SERVICE}</option>
                {services.map((s) => (
                  <option key={s.num}>{s.name}</option>
                ))}
              </Select>
              {status === "error" && (
                <div style={{ fontSize: 13.5, color: colors.red }}>{error}</div>
              )}
              <ConsentCheckbox error={errors.consent} onChange={() => clearFieldError("consent")} style={{ marginTop: 2 }} />
              <button
                type="submit"
                disabled={status === "sending"}
                style={{
                  marginTop: 4,
                  background: colors.red,
                  color: "#fff",
                  border: "none",
                  fontFamily: font.display,
                  fontWeight: 600,
                  fontSize: 16,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  padding: 16,
                  borderRadius: 11,
                  cursor: status === "sending" ? "default" : "pointer",
                  opacity: status === "sending" ? 0.7 : 1,
                }}
              >
                {status === "sending" ? "Отправляем…" : "Отправить заявку"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const fieldStyle = {
  width: "100%",
  padding: "14px 16px",
  border: "1px solid #dde1e7",
  borderRadius: 11,
  fontFamily: font.body,
  fontSize: 15,
  background: "#fff",
  outline: "none",
};

function Input({ invalid, onFocus, onBlur, ...props }) {
  const base = invalid ? colors.red : "#dde1e7";
  return (
    <input
      {...props}
      style={{ ...fieldStyle, borderColor: base }}
      onFocus={(e) => {
        e.target.style.borderColor = colors.red;
        if (onFocus) onFocus(e);
      }}
      onBlur={(e) => {
        e.target.style.borderColor = base;
        if (onBlur) onBlur(e);
      }}
    />
  );
}

function FieldError({ text }) {
  return <div style={{ fontSize: 12.5, color: colors.red, marginTop: 5 }}>{text}</div>;
}

function Select({ name, children }) {
  return (
    <select
      name={name}
      defaultValue=""
      style={{ ...fieldStyle, color: colors.text, cursor: "pointer" }}
      onFocus={(e) => (e.target.style.borderColor = colors.red)}
      onBlur={(e) => (e.target.style.borderColor = "#dde1e7")}
    >
      {children}
    </select>
  );
}
